import { useEffect, useRef, useState } from "react";

const unwrap = (res) => {
  const info = res?.data?.info;
  if (!info || !info.totals) throw new Error("Respuesta inesperada de estadísticas.");
  return info;
};

const errorMessage = (err) => err?.response?.data?.message || "No pudimos cargar las estadísticas.";

/**
 * Loads the statistics of one screen for the current range and, when there is one, the comparison
 * range, both in parallel. Shared by the court and the Beauty statistics screens.
 *
 * - The previous result stays in `data` while a new request is in flight (`loading` is true), so the
 *   screen can dim it instead of flashing a skeleton.
 * - A failed current range clears `data` and sets `error`. A failed comparison keeps the current
 *   numbers and only flags `comparisonFailed`.
 * - Responses that arrive out of order are dropped: only the latest request may write state.
 *
 * `data` also records the ranges and compare mode it answers, so labels cannot drift from the
 * numbers while the filters have already moved on.
 *
 * @param {{enabled: boolean, requestKey: string, fetchRange: (range: {from: string, to: string}) => Promise,
 *   current: {from: string, to: string}, comparison: {from: string, to: string}|null, compareMode: string}} input
 *   `enabled` false keeps the hook idle (no club / salon yet). `requestKey` identifies everything
 *   that is not a range (the entity and its filters): when it changes the data is requested again.
 *   `fetchRange` is called once per range and may change on every render; the latest one is used.
 * @returns {{data: null|{current: object, comparison: object|null, compareMode: string},
 *   loading: boolean, error: string, comparisonFailed: boolean}}
 */
export default function useStatsData({ enabled, requestKey, fetchRange, current, comparison, compareMode }) {
  const [state, setState] = useState({ data: null, loading: Boolean(enabled), error: "", comparisonFailed: false });
  const latest = useRef(0);
  const fetchRef = useRef(fetchRange);
  fetchRef.current = fetchRange;

  const currentFrom = current.from;
  const currentTo = current.to;
  const comparisonFrom = comparison?.from;
  const comparisonTo = comparison?.to;

  useEffect(() => {
    if (!enabled) return undefined;
    const requestId = ++latest.current;
    setState((s) => ({ ...s, loading: true, error: "", comparisonFailed: false }));

    const currentRange = { from: currentFrom, to: currentTo };
    const comparisonRange = comparisonFrom ? { from: comparisonFrom, to: comparisonTo } : null;
    Promise.allSettled([
      fetchRef.current(currentRange),
      comparisonRange ? fetchRef.current(comparisonRange) : Promise.resolve(null),
    ]).then(([cur, cmp]) => {
      if (requestId !== latest.current) return;
      let currentInfo;
      try {
        if (cur.status === "rejected") throw cur.reason;
        currentInfo = unwrap(cur.value);
      } catch (err) {
        setState({ data: null, loading: false, error: errorMessage(err), comparisonFailed: false });
        return;
      }
      let comparisonInfo = null;
      let comparisonFailed = false;
      if (comparisonRange) {
        try {
          if (cmp.status === "rejected") throw cmp.reason;
          comparisonInfo = unwrap(cmp.value);
        } catch (_) {
          comparisonFailed = true;
        }
      }
      setState({
        data: { current: currentInfo, comparison: comparisonInfo, compareMode },
        loading: false,
        error: "",
        comparisonFailed,
      });
    });

    return () => {
      // A newer effect run bumps `latest`; unmounting must also silence the pending request.
      if (latest.current === requestId) latest.current += 1;
    };
  }, [enabled, requestKey, currentFrom, currentTo, comparisonFrom, comparisonTo, compareMode]);

  return state;
}
